import React from "react";
import { Link } from "react-router-dom";

export const AUTH_LOGO_SRC = `${process.env.PUBLIC_URL}/logo192.png`;

export const authLabelClass = "block text-sm font-medium text-slate-600 mb-2";

export const authInputClass =
  "w-full min-w-0 max-w-full px-4 py-3.5 rounded-2xl bg-slate-50 text-slate-900 text-[15px] " +
  "placeholder:text-slate-400 transition-all duration-200 border border-slate-200 " +
  "focus:outline-none focus:bg-white focus:border-teal-700/30 focus:ring-4 focus:ring-teal-700/10 " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

export const authPrimaryButtonClass =
  "w-full inline-flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl bg-teal-700 hover:bg-teal-800 " +
  "active:scale-[0.99] text-white text-[15px] font-semibold shadow-lg shadow-teal-900/20 transition-all " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100";

export const authSecondaryButtonClass =
  "w-full inline-flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl border border-slate-200 bg-white " +
  "hover:bg-slate-50 text-slate-800 text-[15px] font-semibold transition-colors";

export const authLinkClass = "text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors";

export const AuthMessage = ({ variant = "error", children }) => (
  <p
    className={`text-sm font-medium rounded-xl px-4 py-3 ${
      variant === "error" ? "text-red-700 bg-red-50" : "text-teal-800 bg-teal-50"
    }`}
  >
    {children}
  </p>
);

export const AuthBrand = ({ subtitle, dark = true }) => (
  <div className="flex flex-col items-center">
    <img
      src={AUTH_LOGO_SRC}
      alt="Gamrent"
      className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl shadow-lg shadow-black/20"
    />
    <h1 className={`mt-5 sm:mt-6 text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
      Gamrent
    </h1>
    {subtitle ? (
      <p className={`mt-2 text-sm text-center ${dark ? "text-white/60" : "text-slate-500"}`}>{subtitle}</p>
    ) : null}
  </div>
);

export const AuthTabs = ({ active, onSignIn, onSignUp, disabled }) => {
  const tabClass = (isActive) =>
    `flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
      isActive ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="flex p-1 bg-slate-50 rounded-2xl border border-slate-200" role="tablist">
      <button type="button" role="tab" aria-selected={active === "login"} onClick={onSignIn} disabled={disabled} className={tabClass(active === "login")}>
        Sign in
      </button>
      <button type="button" role="tab" aria-selected={active === "signup"} onClick={onSignUp} disabled={disabled} className={tabClass(active === "signup")}>
        Sign up
      </button>
    </div>
  );
};

export const PasswordVisibilityButton = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={visible ? "Hide password" : "Show password"}
    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
  >
    {visible ? (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    )}
  </button>
);

export const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export const AuthFieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="mt-1.5 text-xs font-medium text-red-600">
      {children}
    </p>
  ) : null;

export const AuthFieldHint = ({ id, children }) => (
  <p id={id} className="mt-1.5 text-xs text-slate-400">
    {children}
  </p>
);

export const AuthInputIcon = ({ children }) => (
  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true">
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      {children}
    </svg>
  </span>
);

const AuthShell = ({ subtitle, footnote, wide = false, aside, children }) => (
  <div className="min-h-dvh flex flex-col bg-teal-800 bg-[radial-gradient(ellipse_at_top,rgba(45,212,191,0.18),transparent_60%)]">
    <div className="p-5 sm:p-6 pt-[max(1.25rem,env(safe-area-inset-top))]">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
        ← Back to home
      </Link>
    </div>

    <div className="flex-1 flex flex-col items-center justify-center px-5 sm:px-8 pt-0 pb-[max(2rem,env(safe-area-inset-bottom))]">
      <div
        className={`w-full animate-fade-in ${
          aside ? "max-w-md lg:max-w-5xl lg:grid lg:grid-cols-[1fr_28rem] lg:gap-16 lg:items-center" : wide ? "max-w-md" : "max-w-sm"
        }`}
      >
        {aside ? <div className="hidden lg:block">{aside}</div> : null}

        <div className="min-w-0">
          <div className={`mb-8 ${aside ? "lg:hidden" : ""}`}>
            <AuthBrand subtitle={subtitle} />
          </div>

          <div className="bg-white rounded-3xl shadow-2xl shadow-black/20 p-6 sm:p-8 space-y-5">
            {children}
          </div>

          {footnote ? (
            <p className="mt-6 text-xs text-white/50 text-center leading-relaxed px-4">{footnote}</p>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

export default AuthShell;
